import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
    PhoneAuthParams,
    PhoneNumberParams,
    postPhoneAuthRequest,
    postPhoneNumber,
    postPhoneNumberCall
} from "../actions";
import { push } from "connected-react-router";
import { Route, Switch } from "react-router";
import PhoneNumberScreen from "../components/PhoneNumberScreen";
import PhoneAuthScreen from "../components/PhoneAuthScreen";
import { loggedIn } from "../../util";
import { colors } from "../../constants";
import { fetchRecaptchaToken } from "src/util/recaptcha";

interface Props {
    postPhoneNumber: (phoneNumberParams: PhoneNumberParams) => void;
    postPhoneNumberResend: (phoneNumberParams: PhoneNumberParams) => void;
    postPhoneNumberCall: (phoneNumberParams: PhoneNumberParams) => void;
    postPhoneAuth: (phoneAuthParams: PhoneAuthParams) => void;
    push: (route: string) => void;
}

const PhoneAuth = ({
    postPhoneNumber,
    postPhoneNumberResend,
    postPhoneNumberCall,
    postPhoneAuth,
    push
}: Props) => {
    useEffect(() => {
        if (loggedIn()) {
            push("/");
        }
        document.body.style.backgroundColor = colors.primaryBlue;

        return () => {
            document.body.style.backgroundColor = "";
        };
    }, []);

    const [phoneNumber, setPhoneNumber] = useState("");
    const [authCode, setAuthCode] = useState("");

    useEffect(() => {
        (async () => {
            if (authCode.length === 4 && phoneNumber.length > 0) {
                console.log('[Auth] Confirm code entered, acquiring reCAPTCHA token (phone_auth_confirm)');
                const recaptchaToken = await fetchRecaptchaToken("login");
                console.log('[Auth] reCAPTCHA token (confirm) acquired?', !!recaptchaToken);
                if (!recaptchaToken) {
                    console.warn('[Auth] Blocking confirm dispatch: reCAPTCHA token missing (phone_auth_confirm)');
                    return;
                }
                postPhoneAuth({ phoneNumber, authCode, recaptchaToken });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authCode]);

    return (
        <Switch>
            <Route
                path="/onboarding/phone/auth"
                render={() => {
                    return (
                        <PhoneAuthScreen
                            backClicked={() => push("/onboarding/phone")}
                            phoneNumber={phoneNumber}
                            authCode={authCode}
                            setAuthCode={setAuthCode}
                            resendClicked={async () => {
                                console.log('[Auth] Resend requested, acquiring reCAPTCHA token (phone_auth_resend)');
                                const recaptchaToken = await fetchRecaptchaToken("login");
                                console.log('[Auth] reCAPTCHA token (resend) acquired?', !!recaptchaToken);
                                if (!recaptchaToken) {
                                    console.warn('[Auth] Blocking resend dispatch: reCAPTCHA token missing (phone_auth_resend)');
                                    return;
                                }
                                postPhoneNumberResend({ phoneNumber, recaptchaToken });
                            }}
                            callClicked={async () => {
                                console.log('[Auth] Voice call requested, acquiring reCAPTCHA token (phone_auth_call)');
                                const recaptchaToken = await fetchRecaptchaToken("login");
                                console.log('[Auth] reCAPTCHA token (call) acquired?', !!recaptchaToken);
                                if (!recaptchaToken) {
                                    console.warn('[Auth] Blocking voice call dispatch: reCAPTCHA token missing (phone_auth_call)');
                                    return;
                                }
                                postPhoneNumberCall({ phoneNumber, recaptchaToken });
                            }}
                        />
                    );
                }}
            />
            <Route
                path="/onboarding/phone"
                render={props => {
                    return (
                        <PhoneNumberScreen
                            phoneNumber={phoneNumber}
                            setPhoneNumber={setPhoneNumber}
                            nextClicked={async () => {
                                console.log('[Auth] Start auth requested, acquiring reCAPTCHA token (login)');
                                const recaptchaToken = await fetchRecaptchaToken("login");
                                console.log('[Auth] reCAPTCHA token (start) acquired?', !!recaptchaToken);
                                if (!recaptchaToken) {
                                    console.warn('[Auth] Blocking start dispatch: reCAPTCHA token missing (login)');
                                    return;
                                }
                                postPhoneNumber({ phoneNumber, recaptchaToken });
                            }}
                        />
                    );
                }}
            />
        </Switch>
    );
};

const mapDispatchToProps = (dispatch: any) => ({
    postPhoneNumber: (phoneNumberRequest: PhoneNumberParams) =>
        dispatch(postPhoneNumber(phoneNumberRequest)),
    postPhoneNumberResend: (phoneNumberRequest: PhoneNumberParams) =>
        dispatch(postPhoneNumber(phoneNumberRequest)),
    postPhoneNumberCall: (phoneNumberRequest: PhoneNumberParams) =>
        dispatch(postPhoneNumberCall(phoneNumberRequest)),
    postPhoneAuth: (authRequest: PhoneAuthParams) =>
        dispatch(postPhoneAuthRequest(authRequest)),
    push: (route: string) => dispatch(push(route))
});

export default connect(null, mapDispatchToProps)(PhoneAuth);
